import { useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { apiDelete, apiPatch, type Organization } from "../api";
import { useAuth } from "../auth";
import { Icons } from "../components/icons";
import { AddMemberModal } from "../components/AddMemberModal";
import {
  useGetOrganizationQuery,
  useGetOrgMembersQuery,
  useRemoveOrgMemberMutation,
  useUpdateOrgMemberRoleMutation,
} from "../store/organizations";

type Tab = "members" | "services" | "summary";

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const { refreshUser } = useAuth();
  // RTK Query — `skip` evita el fetch hasta tener id, y los tags invalidan al editar.
  const { data: org, refetch: loadOrg } = useGetOrganizationQuery(id ?? "", { skip: !id });
  const { data: members = [] } = useGetOrgMembersQuery(id ?? "", { skip: !id });
  const [removeOrgMember] = useRemoveOrgMemberMutation();
  const [updateOrgMemberRole] = useUpdateOrgMemberRoleMutation();

  const [tab, setTab] = useState<Tab>("members");
  const [showAddMember, setShowAddMember] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Compat con el resto del archivo que usa `loadMembers()` tras agregar uno.
  // RTK Query invalida el tag automáticamente, pero mantenemos el callback
  // para no romper las prop drillings hacia AddMemberModal.
  const loadMembers = () => { /* RTK Query refresca via tag invalidation */ };

  async function removeMember(userId: string) {
    if (!id) return;
    if (!confirm("¿Quitar este usuario de la organización?")) return;
    try {
      await removeOrgMember({ orgId: id, user_id: userId }).unwrap();
    } catch (err) {
      alert("No se pudo quitar: " + (err instanceof Error ? err.message : "error"));
    }
  }

  if (!org) {
    return (
      <div className="page-shell">
        <header className="page-header">
          <div>
            <h1>Cargando…</h1>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <header className="page-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Link
            to="/organizations"
            style={{ color: "var(--fg-3)", fontSize: 13 }}
          >
            ← Organizaciones
          </Link>
          <h1>{org.name}</h1>
          <div className="sub mono">{org.slug}</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, paddingTop: 8 }}>
          <button className="btn btn-secondary" onClick={() => setShowRename(true)}>Renombrar</button>
          <button className="btn btn-secondary" onClick={() => setShowDelete(true)} style={{ color: "var(--st-major)" }}>Eliminar</button>
        </div>
      </header>

      <nav
        style={{
          display: "flex",
          gap: 4,
          padding: "0 32px",
          borderBottom: "1px solid var(--border-1)",
        }}
      >
        {(
          [
            ["members", "Usuarios"],
            ["services", "Servicios"],
            ["summary", "Resumen"],
          ] as Array<[Tab, string]>
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              background: "transparent",
              border: "none",
              padding: "10px 16px",
              color: tab === k ? "var(--fg-1)" : "var(--fg-3)",
              borderBottom:
                tab === k ? "2px solid var(--accent)" : "2px solid transparent",
              cursor: "pointer",
              fontWeight: tab === k ? 600 : 500,
              fontSize: 13.5,
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <section style={{ padding: "20px 32px" }}>
        {tab === "members" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, fontFamily: "var(--font-display)" }}>
                  Usuarios{" "}
                  <span style={{ color: "var(--fg-3)", fontWeight: 400, fontSize: 13 }}>
                    ({members.length})
                  </span>
                </h3>
                <div className="sub" style={{ marginTop: 2 }}>
                  Quién puede ver y operar dentro de esta organización
                </div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddMember(true)}
              >
                <Icons.Plus size={13} sw={2.2} />
                <span style={{ marginLeft: 6 }}>Agregar usuario</span>
              </button>
            </div>

            <section className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th style={{ width: 160 }}>Rol</th>
                    <th style={{ width: 130 }}>Asignado</th>
                    <th style={{ width: 90, textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const initials = (m.display_name || m.email)
                      .split(/\s+|@/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((s) => s[0]?.toUpperCase())
                      .join("");
                    return (
                      <tr key={m.user_id}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                background: "color-mix(in oklab, var(--accent) 12%, transparent)",
                                color: "var(--accent)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11.5,
                                fontWeight: 700,
                                fontFamily: "var(--font-mono)",
                              }}
                            >
                              {initials}
                            </span>
                            <div>
                              <div style={{ color: "var(--fg-1)", fontWeight: 500 }}>
                                {m.display_name || m.email}
                              </div>
                              {m.display_name && (
                                <div style={{ color: "var(--fg-3)", fontSize: 11.5, marginTop: 1 }}>
                                  {m.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <select
                            className="select"
                            value={m.role}
                            onChange={async (e) => {
                              if (!id) return;
                              const newRole = e.target.value;
                              try {
                                await updateOrgMemberRole({ orgId: id, user_id: m.user_id, role: newRole }).unwrap();
                              } catch (err) {
                                alert("No se pudo actualizar el rol: " + (err instanceof Error ? err.message : "error"));
                              }
                            }}
                            style={{ width: 140 }}
                          >
                            <option value="viewer">viewer</option>
                            <option value="operator">operator</option>
                            <option value="admin">admin</option>
                          </select>
                        </td>
                        <td className="mono" style={{ color: "var(--fg-3)", fontSize: 12 }}>
                          {new Date(m.assigned_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            className="btn btn-icon btn-ghost"
                            type="button"
                            onClick={() => removeMember(m.user_id)}
                            title="Quitar usuario"
                            style={{ color: "var(--st-major)" }}
                          >
                            <Icons.Trash size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {members.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: 0 }}>
                        <div className="empty" style={{ padding: 48 }}>
                          <div className="ico"><Icons.Users size={20} /></div>
                          <div>Sin usuarios todavía.</div>
                          <div className="text-sm" style={{ marginTop: 6 }}>
                            Agregá uno con el botón de arriba — podés crear uno nuevo con contraseña o asignar un usuario existente.
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>
        )}

        {tab === "services" && (
          <div>
            <p style={{ color: "var(--fg-3)" }}>
              Para gestionar los servicios de esta organización, andá a{" "}
              <Link
                to={`/services?org=${org.id}`}
                style={{ color: "var(--accent)" }}
              >
                Servicios → filtrar por {org.name}
              </Link>
              .
            </p>
          </div>
        )}

        {tab === "summary" && (
          <div>
            <p style={{ color: "var(--fg-3)" }}>
              Para ver métricas, andá a{" "}
              <Link
                to={`/traces?org=${org.id}`}
                style={{ color: "var(--accent)" }}
              >
                Traces
              </Link>
              .
            </p>
          </div>
        )}
      </section>

      {showAddMember && id && (
        <AddMemberModal
          orgId={id}
          onClose={() => setShowAddMember(false)}
          onAdded={() => {
            setShowAddMember(false);
            loadMembers();
          }}
        />
      )}

      {showRename && (
        <RenameModal
          org={org}
          onClose={() => setShowRename(false)}
          onRenamed={() => {
            setShowRename(false);
            loadOrg();
            // Refrescá el user para que el OrgSelector tome el nombre nuevo.
            void refreshUser();
          }}
        />
      )}

      {showDelete && (
        <DeleteModal
          org={org}
          onClose={() => setShowDelete(false)}
          onDeleted={async () => {
            // Refrescá el user antes de redirigir para que el OrgSelector ya
            // no muestre la org borrada.
            await refreshUser();
            window.location.href = "/organizations";
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RenameModal
// ---------------------------------------------------------------------------

function RenameModal({ org, onClose, onRenamed }: { org: Organization; onClose: () => void; onRenamed: () => void }) {
  const [name, setName] = useState(org.name);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || name.trim() === org.name) { onClose(); return; }
    setSubmitting(true);
    try {
      await apiPatch(`/v1/organizations/${org.id}`, { name: name.trim() });
      onRenamed();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div onClick={onClose} className="modal-backdrop">
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="modal-card" style={{ width: 440, display: "grid", gap: 14 }}>
        <header><h2 style={{ margin: 0, fontSize: 18 }}>Renombrar organización</h2></header>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Nombre</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          <span className="hint">El slug (<code className="cfg-code">{org.slug}</code>) no se puede cambiar.</span>
        </label>
        {error && <p style={{ color: "var(--st-major)", fontSize: 13, margin: 0 }}>{error}</p>}
        <footer style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? "Guardando…" : "Guardar"}</button>
        </footer>
      </form>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// DeleteModal
// ---------------------------------------------------------------------------

function DeleteModal({ org, onClose, onDeleted }: { org: Organization; onClose: () => void; onDeleted: () => void }) {
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = confirm === org.slug;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canDelete) return;
    setSubmitting(true);
    try {
      await apiDelete(`/v1/organizations/${org.id}`);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div onClick={onClose} className="modal-backdrop">
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="modal-card" style={{ width: 480, display: "grid", gap: 14 }}>
        <header>
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--st-major)" }}>Eliminar organización</h2>
          <p className="text-sm text-muted" style={{ marginTop: 6, marginBottom: 0 }}>
            Esto borra <strong>todos los datos</strong> de <strong>{org.name}</strong>: servicios, traces, incidentes, API keys, suscriptores. No es reversible.
          </p>
        </header>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Confirmá tipeando el slug <code className="cfg-code">{org.slug}</code></span>
          <input className="input mono" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus />
        </label>
        {error && <p style={{ color: "var(--st-major)", fontSize: 13, margin: 0 }}>{error}</p>}
        <footer style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" className="btn btn-danger" disabled={!canDelete || submitting} style={{ background: "var(--st-major)", color: "white" }}>
            {submitting ? "Eliminando…" : "Eliminar definitivamente"}
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
}
